import {
    Amenity,
    NavPoint,
    OfficeUtility,
    OfficeUtilityType,
    Person,
    Room,
    RoomType,
    Seat,
} from "@office-map/api-schema";

import { orgRepository } from "./org.repository";

import {
    commit,
    floors as floorsSource,
    rooms as roomsSource,
    seats as seatsSource,
    navPoints as navPointsSource,
    officeUtilities as officeUtilitiesSource,
    commitDebounced,
} from "@/features/storage/data-provider";

type SeatWithMetadata = Seat & {
    roomName: string;
    roomId: string;
    floorName: string;
    floorId: string;
    occupant: Person | null;
};

class PhysicalRepository {
    seats = seatsSource;
    rooms = roomsSource;
    floors = floorsSource;
    orgRepository = orgRepository;
    navPoints = navPointsSource;
    officeUtilities = officeUtilitiesSource;

    getSeatByEmployeeId = () =>
        Map.groupBy(
            this.seats.filter((seat): seat is Seat & { occupantId: string } => seat.occupantId !== null),
            (seat) => seat.occupantId,
        );
    getRoomByRoomId = () => Map.groupBy(this.rooms, (room) => room.id);
    getFloorByRoomId = () =>
        Map.groupBy(
            this.floors.flatMap((floor) =>
                this.rooms
                    .filter((room) => floor.roomIds.includes(room.id))
                    .map((room) => ({ ...room, floorName: floor.name, floorId: floor.id })),
            ),
            (room) => room.id,
        );

    getSeatDetailsByEmployeeId(employeeId: string): SeatWithMetadata | null {
        const seat = this.getSeatByEmployeeId().get(employeeId)?.[0];
        if (!seat) {
            return null;
        }

        const occupant = this.orgRepository.findEmployeeById(employeeId);

        const room = this.getRoomByRoomId().get(seat.room.id)?.[0];
        const floor = room ? this.getFloorByRoomId().get(room.id)?.[0] : null;
        return {
            ...seat,
            roomName: room ? room.name : "Unknown room",
            roomId: room ? room.id : "unknown-room",
            floorName: floor ? floor.floorName : "Unknown floor", // Simple parsing for demo
            floorId: floor ? floor.floorId : "unknown-floor",
            occupant: occupant,
        };
    }

    getFloorRooms(floorId: string): Room[] | null {
        const roomIds = this.floors.find((floor) => floor.id === floorId)?.roomIds ?? null;
        return this.rooms.filter((room) => roomIds?.includes(room.id)) ?? null;
    }

    getALlRooms(): Room[] | null {
        return this.rooms;
    }

    getRoomSeats(roomId: string): Seat[] | null {
        return this.seats.filter((seat) => seat.room.id === roomId);
    }

    addSeatToRoom(roomId: string, location: { x: number; y: number }): string | null {
        const room = this.getRoomByRoomId().get(roomId)?.[0];
        if (!room) {
            console.error(`Room with ID ${roomId} not found.`);
            return null;
        }

        const id = self.crypto.randomUUID();

        this.seats.push({
            id,
            location,
            theta: 0,
            room: room,
            occupantId: null,
        });

        commit();

        return id;
    }

    removeSeatFromRoom(roomId: string, seatId: string): void {
        const index = this.seats.findIndex((seat) => seat.room.id === roomId && seat.id === seatId);
        if (index !== -1) {
            this.seats.splice(index, 1);
            commit();
        }
    }

    updateSeatLocation(roomId: string, seatId: string, newLocation: { x: number; y: number }): void {
        const seat = this.seats.find((s) => s.room.id === roomId && s.id === seatId);
        if (seat) {
            seat.location = newLocation;
        }

        commitDebounced();
    }

    updateNavPointLocation(id: string, newLocation: { x: number; y: number }): void {
        const point = this.navPoints.find((p) => p.id === id);
        if (point) {
            point.location = newLocation;
        }

        commit();
    }

    addNavPoint(navPoint: NavPoint): void {
        this.navPoints.push(navPoint);
        commit();
    }

    updateSeatTheta(roomId: string, seatId: string, theta: number): void {
        const seat = this.seats.find((s) => s.room.id === roomId && s.id === seatId);
        if (seat) {
            seat.theta = theta;
        }

        commit();
    }

    deleteNavPoint(id: string): void {
        const index = this.navPoints.findIndex((p) => p.id === id);
        if (index !== -1) {
            this.navPoints.splice(index, 1);
        }
        commit();
    }

    assignEmployeeToSeat(seatId: string, employee: Person): void {
        const seat = this.seats.find((s) => s.id === seatId);
        if (seat) {
            seat.occupantId = employee.id;
        }

        commit();
    }

    removeEmployeeFromSeat(seatId: string): void {
        const seat = this.seats.find((s) => s.id === seatId);
        if (seat) {
            seat.occupantId = null;
        }

        commit();
    }

    getSeatDetailsById(seatId: string): SeatWithMetadata | null {
        const seat = this.seats.find((s) => s.id === seatId);
        if (!seat) {
            return null;
        }

        const occupant = seat.occupantId === null ? null : this.orgRepository.findEmployeeById(seat.occupantId);

        const room = this.getRoomByRoomId().get(seat.room.id)?.[0];
        const floor = room ? this.getFloorByRoomId().get(room.id)?.[0] : null;

        return {
            ...seat,
            roomName: room ? room.name : "Unknown room",
            roomId: room ? room.id : "unknown-room",
            floorName: floor ? floor.name : "Unknown floor", // Simple parsing for demo
            floorId: floor ? floor.floorId : "unknown-floor",
            occupant,
        };
    }

    renameRoom(roomId: string, newName: string): void {
        const room = this.rooms.find((r) => r.id === roomId);
        if (room) {
            room.name = newName;
        }

        commit();
    }

    updateRoomAmenities(roomId: string, newAmenities: Amenity[]): void {
        const room = this.rooms.find((r) => r.id === roomId);
        if (room) {
            room.amenities = newAmenities;
        }

        commit();
    }

    updateNumberOfSeats(roomId: string, seats: number): void {
        const room = this.rooms.find((r) => r.id === roomId);
        if (room) {
            room.numberOfSeats = seats;
        }

        commit();
    }

    updateRoomType(roomId: string, newType: RoomType): void {
        const room = this.rooms.find((r) => r.id === roomId);
        if (room) {
            room.type = newType;
            if (newType === "toilet") {
                room.amenities = [];
            }
            if (newType === "meeting-room") {
                room.numberOfSeats = 0;
            }
        }

        commit();
    }

    updateRoomLabelPosition(roomId: string, position: { x: number; y: number }): void {
        const room = this.rooms.find((r) => r.id === roomId);
        if (room) {
            room.labelPosition = position;
        }
        commitDebounced();
    }

    getFloorUtilities(floorId: string): OfficeUtility[] {
        return this.officeUtilities.filter((u) => u.floorId === floorId);
    }

    addUtility(type: OfficeUtilityType, floorId: string, location: { x: number; y: number }): string {
        const id = self.crypto.randomUUID();
        this.officeUtilities.push({ id, type, floorId, location });
        commit();
        return id;
    }

    removeUtility(id: string): void {
        const index = this.officeUtilities.findIndex((u) => u.id === id);
        if (index !== -1) {
            this.officeUtilities.splice(index, 1);
        }
        commit();
    }

    moveUtility(id: string, location: { x: number; y: number }): void {
        const utility = this.officeUtilities.find((u) => u.id === id);
        if (utility) {
            utility.location = location;
        }
        commitDebounced();
    }

    getUtilityById(id: string): OfficeUtility | undefined {
        return this.officeUtilities.find((u) => u.id === id);
    }
}

export const physicalRepository = new PhysicalRepository();
